import { CustomButton, PrimaryButton } from "@/components/ui/Buttons";
import { TNotification } from "@/types/types";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { useMarkAsReadMutation } from "@/redux/feature/notification/notificationApi";
import { handleApiRequest } from "@/utils/handleApiRequest";
import toast from "react-hot-toast";
import { X } from "lucide-react";

const NOTIFICATION_ICON_FALLBACK =
  "https://assets.passionbits.io/platform-data/notification_icon.png";

const notificationIconOnError = (
  e: React.SyntheticEvent<HTMLImageElement, Event>
) => {
  const el = e.currentTarget;
  if (el.src !== NOTIFICATION_ICON_FALLBACK) {
    el.onerror = null;
    el.src = NOTIFICATION_ICON_FALLBACK;
  }
};

const NotificationCard = ({ data }: { data: TNotification }) => {
  return (
    <div>
      {data.type === "order_manage" && (
        <OrderManageNotificationCard data={data} />
      )}
      {data.type === "info" && <InfoManageNotificationCard data={data} />}
      {data.type === "creator" && <InfoManageNotificationCard data={data} />}
    </div>
  );
};

export default NotificationCard;
const smartNavigate = (link: string, navigate: (url: string) => void) => {
  if (!link) return;

  const isExternal = link.startsWith("http://") || link.startsWith("https://");

  if (isExternal) {
    window.location.href = link;
  } else {
    navigate(link);
  }
};

const OrderManageNotificationCard = ({ data }: { data: TNotification }) => {
  const { fileIcon, videoTitle, projectTitle, desc } = data.data;
  const relativeTime = moment(data?.createdAt).fromNow();
  const [markAsRead] = useMarkAsReadMutation();
  const navigate = useNavigate();
  const handleAction = async (link: string) => {
    // mark as read before redirect

    if (!data.isRead) {
      const payload = {
        id: data._id,
      };
      await handleApiRequest({
        payload: payload,
        requestFunction: markAsRead,
        onSuccess: () => {
          //redirect
          // navigate(link);
          smartNavigate(link, navigate);
        },
        onError: () => {
          toast.error("Something Went Wrong");
        },
      });
      return;
    } else {
      // navigate(link);
      smartNavigate(link, navigate);
    }
  };

  const handlerMarkAsRead = async () => {
    if (!data.isRead) {
      const payload = {
        id: data._id,
      };
      await handleApiRequest({
        payload: payload,
        requestFunction: markAsRead,
        onSuccess: () => {
          //redirect
          // navigate(link);
        },
        onError: () => {
          toast.error("Something Went Wrong");
        },
      });
      return;
    }
  };

  return (
    <div
      className={`${
        !data.isRead && ""
      } p-3 w-full flex gap-x-4 hover:bg-[#FEF6F0] border border-[#E5E9EB] rounded-lg mb-9 group transition-all duration-200 ease-in-out`}
    >
      <div className="min-w-[48px] max-w-[48px]">
        <img
          src={fileIcon}
          alt="file_icon"
          onError={notificationIconOnError}
        />
      </div>
      <div className="w-full">
        <div className="flex w-full items-center justify-between">
          <h2 className="text-[#222222] text-sm tracking-[0%] leading-[18px] font-semibold">
            {data.title}
          </h2>
          <div className="relative flex items-center justify-end min-w-fit">
            <p className="text-[12px] text-[#747992] group-hover:opacity-0 transition-opacity duration-200 ease-in-out">
              {relativeTime}
            </p>
            <div onClick={handlerMarkAsRead}>
              <X
                color="#222222"
                className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out cursor-pointer hover:scale-110 transform"
                size={16}
              />
            </div>
          </div>
        </div>
        <p className="text-[#717171] mt-2 text-[13px] leading-[18px] tracking-[0%]">
          {projectTitle} / {videoTitle}
        </p>
        <div
          dangerouslySetInnerHTML={{ __html: desc }}
          className="text-[12px] md:text-[13px] pb-5 text-[#717171] font-medium pt-4 md:leading-[22px] tracking-[-0.5%]"
        ></div>
        <div className="flex gap-2 flex-wrap">
          {data.actions.map((item, i) => (
            <CustomButton
              key={i}
              onClick={() => handleAction(item.link)}
              variant="outline"
              backgroundColor="#17457D"
              border="1px solid #D0D5DD"
              color="#000000"
              borderRadius="10px"
              fontSize="14px"
              padding="8px 12px"
              fontWeight="500"
              className="leading-5 tracking-[-0.75%] transition-all duration-150 ease-in-out hover:shadow-sm hover:scale-[1.02] active:scale-[0.98] notification-btn-shadow"
            >
              {item.label}
            </CustomButton>
          ))}
        </div>
      </div>
    </div>
  );
};

const InfoManageNotificationCard = ({ data }: { data: TNotification }) => {
  const { fileIcon, videoTitle, projectTitle, desc } = data.data;
  const relativeTime = moment(data?.createdAt).fromNow();
  const [markAsRead] = useMarkAsReadMutation();
  const navigate = useNavigate();
  const handleAction = async (link: string) => {
    // mark as read before redirect

    if (!data.isRead) {
      const payload = {
        id: data._id,
      };
      await handleApiRequest({
        payload: payload,
        requestFunction: markAsRead,
        onSuccess: () => {
          //redirect
          // navigate(link);
          smartNavigate(link, navigate);
        },
        onError: () => {
          toast.error("Something Went Wrong");
        },
      });
      return;
    } else {
      // navigate(link);
      smartNavigate(link, navigate);
    }
  };
  return (
    <div
      className={`${
        !data.isRead && ""
      } p-3 w-full flex gap-x-4 hover:bg-[#FEF6F0] border border-[#E5E9EB] rounded-lg mb-9 group transition-all duration-200 ease-in-out`}
    >
      <div className="min-w-[48px] max-w-[48px]">
        <img
          src={fileIcon}
          alt="file_icon"
          onError={notificationIconOnError}
        />
      </div>
      <div className="w-full">
        <div className="flex w-full items-center justify-between">
          <h2 className="text-[#222222] text-sm tracking-[0%] leading-[18px] font-semibold">
            {data.title}
          </h2>
          <div className="relative flex items-center justify-end min-w-fit">
            <p className="text-[12px] text-[#747992] group-hover:opacity-0 transition-opacity duration-200 ease-in-out">
              {relativeTime}
            </p>
            <X
              color="#222222"
              className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out cursor-pointer hover:scale-110 transform"
              size={16}
            />
          </div>
        </div>

        <div
          dangerouslySetInnerHTML={{ __html: desc }}
          className="text-[12px] md:text-[13px] pb-5 text-[#717171] font-medium pt-4 md:leading-[22px] tracking-[-0.5%]"
        ></div>
        <div className="flex gap-2 flex-wrap">
          {data.actions.map((item, i) => (
            <CustomButton
              key={i}
              onClick={() => handleAction(item.link)}
              variant="outline"
              backgroundColor="#17457D"
              border="1px solid #D0D5DD"
              color="#000000"
              borderRadius="10px"
              fontSize="14px"
              padding="8px 12px"
              fontWeight="500"
              className="leading-5 tracking-[-0.75%] transition-all duration-150 ease-in-out hover:shadow-sm hover:scale-[1.02] active:scale-[0.98] notification-btn-shadow"
            >
              {item.label}
            </CustomButton>
          ))}
        </div>
      </div>
    </div>
  );
};
